// 通话 (office/call.ts): a group call, like a group video call, wherever the otters are (other rooms, far apart, another
// floor). Start it from a person's card (通话), the toolbar's 通话 (the members picker, several at once), or, in a call,
// 添加 at the foot of the call view's 参会者 list. The picker lists everyone of this server online: this boat's people, and (call_who) those on
// the other boats and the 活动舱, each with where they are. The one rung gets a card 「X 邀请你通话」 with 接听 / 拒绝, a ring tone (none on 请勿打扰) and
// RING_MS to answer; 接听 joins from wherever they stand. The call view is the meeting view (public/meeting.js) with the
// call's people as tiles (camera on or their otter, a green ring while they talk, screen share on the stage), and 地图
// shrinks it to the small floating window so they can keep walking; 挂断 leaves. The call ends when fewer than two are
// left. People on another floor are tiles too: their mic, camera and screen come straight from the call (remote(),
// pulled by app.js like anyone's), never their otter on this map.
// The call's id and hub are kept in sessionStorage, so a reload or another floor (which reloads the page) comes back
// into it ({t:"call_resume"}).
// Voice while in a call: everyone in it at full volume; people outside it only right next to me, quieter (app.js
// loudness; the office decides who that is, voice.ts).
// 会议室 and 通话 don't mix (artin 11-03; office/call.ts): in a meeting room 通话 (the bar, a card, 接听) is off with
// 「会议室里请直接邀请对方进会议」, and walking into one while in a call asks first (mayEnter: 进入并结束通话 hangs up and
// walks me in; 取消 keeps me at the door). Whatever gets me into a room anyway, the office hangs up for me (E_ROOM_ENDED).
import { icon, put } from '/icons.js';
import { t } from '/i18n.js';

const KEY = 'deck.call';
const ERR = { E_SELF: '这是你自己', E_HERE: '已经在通话里', E_BUSY: 'TA 正在别的通话或游戏里', E_PENDING: '正在呼叫 TA', E_FULL: '通话人数满了',
  E_RATE: '呼叫得太勤了，歇一会儿', E_FORBIDDEN: '不能发起通话', E_INVALID: '没选人', E_UNAVAILABLE: '通话暂时连不上，再试一次' };
/** A refusal's words (E_NOT_FOUND: also someone on another boat who left). */
const KEYED = new Set(['E_NOT_FOUND', 'E_IN_ROOM', 'E_THEY_IN_ROOM', 'E_ROOM_ENDED']);
const errSay = (code) => (KEYED.has(code) ? t(`call.err.${code}`) : ERR[code]);
const RING_SAY = { declined: '拒绝了通话', expired: '没有接听', left: '离开了办公室' };
const END_SAY = { unanswered: '没有人接听，通话结束', alone: '其他人都挂断了，通话结束', gone: '通话已经结束' };

/**
 * @param o {send, toast, me, peers, members (public/members.js), floorName(room) -> '2F' | null, guest: boolean,
 *   dropMedia(p): stop pulling a remote member's tracks (app.js), changed(): redraw what depends on the call,
 *   zoneAt(x, y): the zone a spot is in (by the feet), walkToZone(id, name): walk me into a zone (members.js)}
 */
export function initCall(o) {
  let call = null;            // my call, as the office last said ({t:'call'}), or null
  const remote = new Map();   // 'c:' + mid -> a member on another floor, shaped like a peer (for app.js's pulls and the tiles)
  const ringsSeen = new Map(); // my rings' last status (ring id -> status), to say how each went once
  const ringing = new Map();  // rings to me now: id -> {quiet}
  let tone = null;
  let far = [];               // people on the other boats (call_who), shaped like peers: {id: 'f:<room>/<peer>', room, peer, ...}

  const save = () => { try { if (call) sessionStorage.setItem(KEY, JSON.stringify({ id: call.id, host: call.host })); else sessionStorage.removeItem(KEY); } catch {} };
  // a floor this page doesn't know yet (made since it loaded): read the list again, then rename
  const where = (m) => (m.here ? '' : o.floorName(m.room) || (o.refreshFloors?.().then(() => { if (o.floorName(m.room)) { syncRemote(); o.changed(); } }), t('call.where.otherBoat')));

  /** The members on other floors, as peers (kept object by object: app.js keeps its pull state on them). */
  function syncRemote() {
    const want = new Set();
    for (const m of call?.members ?? []) {
      if (m.here || m.mid === call.me) continue;
      const id = 'c:' + m.mid; want.add(id);
      let p = remote.get(id);
      if (p && p.sessionId !== m.sid) { o.dropMedia(p); p = null; } // a new voice session (they reloaded): pull afresh
      if (!p) { p = { id, remote: true, x: 0, y: 0 }; remote.set(id, p); }
      Object.assign(p, { name: `${m.name} · ${where(m)}`, kind: m.kind, seed: m.mid, look: m.look, sessionId: m.sid, trackName: m.mic, camTrack: m.cam, screenTrack: m.screen, muted: m.muted, gone: !!m.gone, guest: m.guest ? {} : undefined });
      if (!m.cam && p.camStream) p.camStream = null;
    }
    for (const [id, p] of remote) if (!want.has(id)) { o.dropMedia(p); remote.delete(id); }
  }

  // ---- the ring tone (a card to me, not on 请勿打扰): two soft notes every 2 s while it shows
  function ringTone(on) {
    if (!on) { clearInterval(tone); tone = null; return; }
    if (tone) return;
    const play = () => {
      try {
        const A = (ringTone.ctx ??= new (window.AudioContext || window.webkitAudioContext)());
        A.resume?.().catch(() => {});
        const t0 = A.currentTime;
        for (const [f, dt] of [[659.3, 0], [880, 0.18], [659.3, 0.5], [880, 0.68]]) {
          const osc = A.createOscillator(), g = A.createGain();
          osc.type = 'triangle'; osc.frequency.value = f;
          g.gain.setValueAtTime(0.0001, t0 + dt); g.gain.exponentialRampToValueAtTime(0.16, t0 + dt + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, t0 + dt + 0.3);
          osc.connect(g).connect(A.destination); osc.start(t0 + dt); osc.stop(t0 + dt + 0.32);
        }
      } catch {}
    };
    play(); tone = setInterval(play, 2000);
  }
  const toneCheck = () => ringTone([...ringing.values()].some((r) => !r.quiet && r.expires > Date.now()));
  setInterval(toneCheck, 1000);

  /** Ring these people (connection ids on this floor, or 'f:<room>/<peer>' on another boat): a new call, or into mine. */
  function ring(ids) {
    const to = ids.map((id) => { const f = far.find((p) => p.id === id); return f ? { room: f.room, peer: f.peer } : id; });
    if (to.length) o.send({ t: 'call_ring', to });
  }
  const boat = (room) => o.floorName(room) || t('call.where.otherBoat');
  // walking into a 会议室 in a call (mayEnter): the card is up (its timer); 取消 just now; 进入并结束通话 lets me into that
  // room; when the office said it hung up for me because I'm in one (E_ROOM_ENDED; then call_end says nothing more)
  let asking = null, quietUntil = 0, pass = null, roomEnded = 0;
  const stopAsking = () => { clearTimeout(asking); asking = null; };
  /** The 会议室 I stand in, or null: no 通话 from there. */
  const room = () => { const me = o.me(), z = me && o.zoneAt?.(me.x, me.y); return z?.meeting ? z : null; };
  function hangup() { if (call) { o.beforeHangup?.(); o.send({ t: 'call_leave' }); } }
  /** My call ended because of a 会议室 (I'm in one, or on my way in after 进入并结束通话): one toast, saying so. */
  const forRoom = () => !!room() || (!!pass && Date.now() < pass.until);
  /** The picker: everyone of this server online (this boat's people, then the other boats'), not already in my call
   *  (or being rung). */
  function pick() {
    if (o.guest) { o.toast('访客不能发起通话'); return; }
    if (!call && room()) { o.toast(t('call.err.E_IN_ROOM'), null, 'door'); return; }
    const key = (p) => (p.far ? p.room + '/' + p.peer : null);
    const inCall = (p) => !!call && call.members.some((m) => (p.far ? m.far === key(p) : m.here && m.peer === p.id));
    const rung = (p) => !!call && call.rings.some((r) => r.status === 'ringing' && (p.far ? r.far === key(p) : r.peer === p.id));
    o.send({ t: 'call_who' }); // (the last list stays until the answer comes)
    o.members.pick({
      title: () => (call ? '添加到通话' : '发起通话'), icon: 'phone', sendLabel: call ? '呼叫' : '发起通话', max: 8, closeOnSend: true,
      hint: t('call.pick.hint'),
      emptyText: t('call.pick.empty'),
      extra: () => far,
      exclude: (p) => inCall(p),
      top: call && o.link && !o.guest ? () => o.link.top('call') : undefined, // 复制参会链接 (public/meetlink.js), once in a call
      // (someone in a 会议室 can't be rung: office/call.ts E_THEY_IN_ROOM)
      state: (p) => (rung(p) ? { text: '呼叫中', cls: 'wait', lock: true } : p.far && p.inCall ? { text: t('call.pick.inCall'), cls: 'off', lock: true }
        : !p.far && o.zoneAt?.(p.x, p.y)?.meeting ? { text: t('call.pick.inRoom'), cls: 'off', lock: true } : null),
      onSend: (ids) => ring(ids),
    });
  }

  function msg(m) {
    if (m.t === 'call_who') {
      far = (Array.isArray(m.people) ? m.people : []).filter((x) => x && typeof x.room === 'string' && typeof x.peer === 'string')
        .map((x) => ({ id: `f:${x.room}/${x.peer}`, far: true, room: x.room, peer: x.peer, name: String(x.name ?? ''), kind: x.kind === 'agent' ? 'agent' : 'human', status: x.status ?? undefined, inCall: !!x.call, where: boat(x.room) }));
      o.members.refresh();
      // a boat made since this page loaded: read the list once, then name them (never again from here)
      if (far.some((p) => !o.floorName(p.room))) o.refreshFloors?.().then(() => { far = far.map((p) => ({ ...p, where: boat(p.room) })); o.members.refresh(); });
    } else if (m.t === 'call') {
      const first = !call || call.id !== m.id;
      if (first) roomEnded = 0; // (a new call: an earlier one's room ending says nothing about it)
      call = m; save(); syncRemote();
      for (const r of m.rings) {
        const was = ringsSeen.get(r.id); ringsSeen.set(r.id, r.status);
        if (!r.mine || was === r.status) continue;
        if (r.status === 'joined' && was) o.toast(`${r.toName} 接听了`, null, 'phone');
        else if (RING_SAY[r.status]) o.toast(`${r.toName} ${RING_SAY[r.status]}`, null, 'phone');
      }
      if (first && m.members.length > 1) o.toast('通话已接通', null, 'phone');
      o.changed();
    } else if (m.t === 'call_end') {
      if (call && m.id && call.id !== m.id) return;
      const had = !!call;
      for (const r of m.rings ?? []) if (r.mine && ringsSeen.get(r.id) !== r.status && RING_SAY[r.status]) o.toast(`${r.toName} ${RING_SAY[r.status]}`, null, 'phone');
      call = null; save(); syncRemote();
      stopAsking(); o.members.dropCard('callroom'); // (its question is moot: no call to end)
      if (!had || Date.now() - roomEnded < 10_000) {} // the office already said why (E_ROOM_ENDED)
      else if (m.why !== 'left') o.toast(END_SAY[m.why] ?? '通话结束了', null, 'hangup');
      else if (forRoom()) { roomEnded = Date.now(); o.toast(errSay('E_ROOM_ENDED'), null, 'door'); }
      else o.toast('已挂断', null, 'hangup');
      o.changed();
    } else if (m.t === 'call_ring') {
      const key = 'c:' + m.id, who = m.from || '水獭', others = (m.members ?? []).filter((n) => n !== who);
      ringing.set(m.id, { quiet: !!m.quiet, expires: m.expires });
      const card = {
        id: key, icon: 'phone', anytime: true, silent: true, yes: '接听', yesIcon: 'phone', no: '拒绝', noIcon: 'hangup',
        text: others.length ? `${who} 邀请你加入通话（还有 ${others.slice(0, 3).join('、')}${others.length > 3 ? ' 等' : ''}）` : `${who} 邀请你通话`,
        quiet: !!m.quiet, expires: m.expires,
        // in a 会议室: not from here (the card stays, to answer from outside the door)
        accept: () => { if (room()) { o.toast(t('call.err.E_IN_ROOM'), null, 'door'); o.members.addCard(card); return; } ringing.delete(m.id); toneCheck(); o.send({ t: 'call_answer', id: m.id, accept: true }); },
        decline: () => { ringing.delete(m.id); toneCheck(); o.send({ t: 'call_answer', id: m.id, accept: false }); },
      };
      o.members.addCard(card);
      toneCheck();
    } else if (m.t === 'call_ring_gone') {
      ringing.delete(m.id); toneCheck(); o.members.dropCard('c:' + m.id);
      if (m.status === 'cancelled') o.toast('对方已经挂断了', null, 'hangup');
    } else if (m.t === 'call_ok') {
      const bad = m.refused ?? [];
      if (m.sent?.length) o.toast(`正在呼叫 ${m.sent.join('、')}${bad.length ? `；${bad.length} 人没呼出去` : ''}`, null, 'phone');
      else if (bad.length) o.toast(bad.length === 1 ? `${bad[0].toName ? bad[0].toName + '：' : ''}${errSay(bad[0].code) ?? '没呼出去'}` : `没呼出去：${bad.map((r) => `${r.toName ?? '?'} ${errSay(r.code) ?? ''}`).join('，')}`);
    } else if (m.t === 'call_err' && m.code === 'E_ROOM_ENDED') {
      // said once: by call_end just now (forRoom), or here first and call_end then stays quiet
      if (call || Date.now() - roomEnded >= 10_000) o.toast(errSay(m.code), null, 'door');
      roomEnded = Date.now();
    } else if (m.t === 'call_err' && KEYED.has(m.code)) o.toast(errSay(m.code), null, 'door');
    else if (m.t === 'call_err') o.toast(m.code === 'E_GONE' ? '这个通话已经结束了' : m.code === 'E_FULL' ? '通话人数满了' : m.code === 'E_UNAVAILABLE' ? '通话暂时连不上，刷新一下再试' : '没接通，再试一次');
  }

  return {
    msg, ring, hangup, pick,
    /** Going to another boat (the page reloads there): in a call, the office keeps me longer meanwhile. */
    moving() { if (call) o.send({ t: 'call_moving' }); },
    /** A (re)connect was welcomed: back into the call I was in (a reload, another floor). */
    welcome() {
      let s = null; try { s = JSON.parse(sessionStorage.getItem(KEY) ?? 'null'); } catch {}
      if (s?.id && s?.host) o.send({ t: 'call_resume', id: s.id, host: s.host });
    },
    /** The page stops (signed out, another tab): forget the call here (the office drops me after a while). */
    stop() { call = null; for (const [id, p] of remote) { o.dropMedia(p); remote.delete(id); } ringing.clear(); toneCheck(); },
    active: () => (call && call.members.length > 0 ? call : null),
    /** No 通话 to start here (I'm in a 会议室 and not in one): the bar's 通话 shows it off, with the hint. */
    roomBlocked: () => !call && !!room(),
    /** The mover's gate (deck/move.js mayEnter): a step from (x0, y0) to (x, y) into a 会议室 while I'm in a call (or
     *  ringing) isn't taken; it asks once, 进入并结束通话 / 取消, on a card (members.js), and 进入 hangs up and walks me in. */
    mayEnter(x0, y0, x, y) {
      const z = o.zoneAt?.(x, y), now = Date.now();
      if (!call || !z?.meeting || o.zoneAt(x0, y0)?.id === z.id || (pass?.id === z.id && now < pass.until)) return true;
      // asked once per push at the door: 取消 with the keys still held doesn't ask again at once (a second)
      if (!asking && now >= quietUntil) {
        asking = setTimeout(() => { asking = null; }, 30_000); // (the card's own life)
        o.members.addCard({ id: 'callroom', icon: 'door', anytime: true, silent: true, prio: 3, text: t('call.room.ask'), left: () => '', expires: now + 30_000,
          yes: t('call.room.enter'), yesIcon: 'door', no: t('call.room.cancel'), noIcon: 'close',
          accept: () => { stopAsking(); pass = { id: z.id, until: Date.now() + 10_000 }; hangup(); o.walkToZone?.(z.id, z.name); },
          decline: () => { stopAsking(); quietUntil = Date.now() + 1_000; } });
      }
      return false;
    },
    /** Members on other floors, as peers. */
    remote: () => remote,
    /** Is connection id `id` (on this floor) or remote id `c:<mid>` in my call? */
    has(id) {
      if (!call) return false;
      if (remote.has(id)) return true;
      return call.members.some((m) => m.here && m.peer === id && m.mid !== call.me && !m.gone);
    },
    /** For the meeting view (public/meeting.js): the call's people other than me ({id, p}), and who is being rung. */
    view() {
      if (!call) return null;
      const list = [], peers = o.peers();
      for (const m of call.members) {
        if (m.mid === call.me) continue;
        if (m.here) { const p = peers.get(m.peer); if (p) list.push({ id: p.id, p }); else list.push({ id: 'c:' + m.mid, p: { id: 'c:' + m.mid, name: `${m.name}（重连中）`, kind: m.kind, seed: m.mid, look: m.look, gone: true } }); }
        else { const p = remote.get('c:' + m.mid); if (p) list.push({ id: p.id, p: p.gone ? { ...p, name: `${p.name}（重连中）` } : p }); }
      }
      const rung = call.rings.filter((r) => r.status === 'ringing').map((r) => r.toName);
      return { id: call.id, list, rung };
    },
    /** 通话 on someone's card (greet.js, the 小窗's card): ring them, or (in my call already) say so. */
    forCard(p) {
      const me = o.me(); if (!me || p.id === me.id || o.guest) return null;
      if (call?.members.some((m) => m.here && m.peer === p.id)) return { icon: 'phone', label: t('call.card.inCall'), disabled: true };
      if (call?.rings.some((r) => r.status === 'ringing' && r.peer === p.id)) return { icon: 'phone', label: t('call.card.ringing'), disabled: true };
      if (!call && room()) return { icon: 'phone', label: t('bar.call.idle'), disabled: true, title: t('call.err.E_IN_ROOM') };
      if (o.zoneAt?.(p.x, p.y)?.meeting) return { icon: 'phone', label: t('call.pick.inRoom'), disabled: true, title: t('call.err.E_THEY_IN_ROOM') };
      return { icon: 'phone', label: t(call ? 'call.card.add' : 'call.card.call'), title: t(call ? 'call.card.addTip' : 'call.card.callTip'), go: () => ring([p.id]) };
    },
  };
}
